import {
  defaultIdFactory,
  emptyLayout,
  LAYOUT_SCHEMA_VERSION,
  listAllPanelInstances,
  type Layout,
} from "@/shell/layout";
import {
  KIOSK_SHIFT_GATED_ROLES,
  KIOSK_TABS,
  type KioskRole,
} from "@/shell/render/kioskRoles";

import type { Workspace } from "./types";

/* ============================================================ */
/* Builders — le code qui produit chaque preset layout            */
/* ============================================================ */

/**
 * Default : canvas vide. L'utilisateur clique sur "+ Ajouter un panneau"
 * dans la TopBar (pattern TweetDeck) pour le peupler.
 */
function buildDefault(): Layout {
  return emptyLayout();
}

/**
 * Kiosque — le layout FIXE d'un rôle enfermé dans sa barre d'onglets, injecté
 * comme override in-memory par `useKioskLayout` (cf atoms.ts). Quels onglets,
 * pour quel rôle : `render/kioskRoles.ts` (`KIOSK_TABS`) — jamais ici.
 *
 * UN SEUL panneau, N onglets.
 * Pas de colonne fixe à droite, et c'est le point : ouvrir une conversation
 * pousse le fil au milieu et la fiche du fan à droite (cf `openFanTab.ts`).
 * Un Personnel qui aurait gardé sa colonne serait venu se coller en
 * quatrième, et il ne serait rien resté pour le chat. À gauche en onglet, le
 * chatteur bascule dessus quand il veut, sans rien coûter au fil.
 *
 * Ça règle aussi la dérive des largeurs : tant qu'aucune conv n'est ouverte
 * la racine n'est PAS un split, donc rien à re-normaliser. À la fermeture du
 * dernier onglet fan, `cleanupNode` retrouve un split à un seul enfant et le
 * remonte tel quel — le kiosk revient tout seul à sa forme nue.
 *
 * `withConversations` — chez le CHATTEUR, l'onglet Conversations n'existe que
 * si un shift lui est ATTRIBUÉ (décision de Kayla, 18-08-2026). Sans shift il
 * ne voit que Personnel et Documentation : le travail n'apparaît qu'avec le
 * créneau. Ne pas confondre avec le shift gate, qui décide de la FENÊTRE
 * (suis-je dedans, ai-je cliqué Start) et couvre l'onglet de son overlay le cas
 * échéant — ici on parle de l'existence même de l'onglet. Le manager, lui,
 * l'a toujours (cf `KIOSK_TABS`).
 *
 * Le rôle ne peut fermer ni déplacer ces onglets (cf le verrou dans
 * `render/TabStrip.tsx`) ; seul ce qu'il OUVRE ensuite porte une croix.
 */
export function buildKiosk(
  role: KioskRole,
  options?: { withConversations?: boolean },
): Layout {
  // Le drapeau ne mord que sur les rôles dont l'onglet DÉPEND d'un shift. Un
  // manager appelé avec `withConversations: false` garde Conversations : sinon
  // le builder se fierait à son appelant pour tenir un invariant de rôle.
  const dropConversations =
    options?.withConversations === false && KIOSK_SHIFT_GATED_ROLES.has(role);
  const tabs = KIOSK_TABS[role].filter(
    (panelId) => !dropConversations || panelId !== "conversations",
  );
  return {
    version: LAYOUT_SCHEMA_VERSION,
    root: {
      kind: "tabs",
      id: defaultIdFactory(),
      activeIndex: 0,
      panels: tabs.map((panelId) => ({ id: defaultIdFactory(), panelId })),
    },
  };
}

/**
 * Le kiosque à poser, à partir de celui DÉJÀ en place.
 *
 * 🪤 `buildKiosk` fabrique des ids frais et zéro conversation ouverte :
 * l'appeler sur un chatteur en plein travail lui reprend ses onglets fan. Or
 * l'effet qui le pose (cf `useKioskLayout`) se recalcule à chaque MAJ des
 * queries Convex dont il dépend chez un rôle shift-gated — et une simple
 * reconnexion du websocket (veille de la machine, coupure Wi-Fi, redémarrage
 * du backend) les fait toutes repasser par `undefined`. Reconstruire à chaque
 * passage revenait à vider l'écran du chatteur au hasard des aléas réseau.
 * Signalé par Kayla le 10-09-2026 : « d'un coup elle cache la discussion, puis
 * elle réapparaît ».
 *
 * On ne reconstruit donc QUE si les onglets de kiosque du rôle changent
 * vraiment — en pratique, l'apparition ou le retrait de `conversations`. Les
 * onglets `fan` ouverts n'entrent pas dans la comparaison : ils ne sont pas des
 * onglets de kiosque, et c'est justement ce qu'on veut préserver.
 */
export function nextKiosk(
  current: Layout | null,
  role: KioskRole,
  options?: { withConversations?: boolean },
): Layout {
  const cible = buildKiosk(role, options);
  if (current && ongletsDeKiosque(current, role) === ongletsDeKiosque(cible, role)) {
    return current;
  }
  return cible;
}

/** Les onglets de kiosque du rôle présents dans ce layout, dans l'ordre. */
function ongletsDeKiosque(layout: Layout, role: KioskRole): string {
  const presents = new Set(
    listAllPanelInstances(layout).map((p) => p.panelId as string),
  );
  return KIOSK_TABS[role].filter((id) => presents.has(id)).join(",");
}

/**
 * Map { presetId → builder }. Utilise par `actionResetToPreset` pour
 * reconstruire un preset modifie a son etat d'origine.
 */
export const PRESET_LAYOUTS: Record<string, () => Layout> = {
  default: buildDefault,
};

/**
 * Construit l'etat initial des workspaces (presets a la premiere visite).
 * Apres ca, atomWithStorage prend le relais et persiste les modifs en localStorage.
 */
export function buildInitialWorkspaces(): Record<string, Workspace> {
  const now = Date.now();
  return {
    default: {
      id: "default",
      name: "Default",
      description:
        "Empty canvas — click + Add a panel to start",
      layout: buildDefault(),
      isPreset: true,
      createdAt: now,
      updatedAt: now,
    },
  };
}

/** ID du workspace actif au tout premier load (avant que l'user ait switche). */
export const DEFAULT_ACTIVE_WORKSPACE_ID = "default";
